/**
 * Course-correction sprint — Field Records Panel.
 *
 * Real production field tooling. Mounts as a side panel anywhere a sheet
 * is open. Captures photos (camera or upload) + voice notes (MediaRecorder
 * with Web Speech transcript when available) anchored to the sheet +
 * optional sheet-normalised location (nx, ny) + optional GPS.
 *
 * No demo data. No fake transcription. Plain construction-language errors
 * on every failure path.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Camera, Mic, Send, X, Loader2, AlertOctagon, Square, Play, Pause,
  Image as ImageIcon, Crosshair, MapPin, CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";


/**
 * @param {{ sheetId: string, projectId?: string, anchor?: {nx:number,ny:number}|null,
 *           onAnchorRequest?: ()=>void, onClose?: ()=>void }} props
 */
export default function FieldRecordsPanel({ sheetId, projectId, anchor, onAnchorRequest, onClose }) {
  const [photos, setPhotos] = useState([]);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(null); // 'photo' | 'voice' | null
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [p, v] = await Promise.all([
        api.get(`/sheets/${sheetId}/field-photos`),
        api.get(`/sheets/${sheetId}/field-voice-notes`),
      ]);
      setPhotos(p.data?.photos || []);
      setVoices(v.data?.voice_notes || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load field records.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (sheetId) reload(); /* eslint-disable-line */ }, [sheetId]);

  // ----- Photo upload -----
  const onPickPhoto = () => fileRef.current?.click();
  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCapturing("photo");
    try {
      const form = new FormData();
      form.append("file", file);
      if (anchor) {
        form.append("nx", String(anchor.nx));
        form.append("ny", String(anchor.ny));
      }
      form.append("source", "camera");
      // Best-effort GPS — single shot, 5s timeout, no retry.
      try {
        const pos = await new Promise((res, rej) =>
          navigator.geolocation?.getCurrentPosition(res, rej, { timeout: 5000, maximumAge: 30000 }) ?? rej());
        form.append("gps_lat", String(pos.coords.latitude));
        form.append("gps_lng", String(pos.coords.longitude));
        if (pos.coords.accuracy) form.append("gps_accuracy_m", String(pos.coords.accuracy));
      } catch { /* GPS optional */ }
      await api.post(`/sheets/${sheetId}/field-photos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      confirm.raw?.success?.("Photo saved");
      await reload();
    } catch (e) {
      const msg = e?.response?.data?.detail || "PHOTO UPLOAD FAILED — RETRY";
      confirm.raw?.error?.(msg);
    } finally {
      setCapturing(null);
    }
  };

  const sendPhoto = async (id) => {
    try {
      await api.post(`/field-photos/${id}/send-to-foreman`, {});
      confirm.raw?.success?.("Photo sent to foreman");
      reload();
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Send failed");
    }
  };
  const sendVoice = async (id) => {
    try {
      await api.post(`/field-voice-notes/${id}/send-to-foreman`, {});
      confirm.raw?.success?.("Voice note sent to foreman");
      reload();
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Send failed");
    }
  };

  return (
    <div className="flex flex-col h-full bg-[var(--fop-surface)] border-l border-[var(--fop-border)] w-[360px]" data-testid="field-records-panel">
      <header className="px-3 py-2 border-b border-[var(--fop-border)] flex items-center gap-2">
        <Camera size={14} className="text-[var(--fop-primary)]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
          Field Records
        </span>
        {anchor ? (
          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono text-[var(--fop-text-3)]" data-testid="field-records-anchor">
            <Crosshair size={11} /> {anchor.nx.toFixed(2)} · {anchor.ny.toFixed(2)}
          </span>
        ) : (
          <button
            type="button" onClick={onAnchorRequest}
            className="ml-2 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
            data-testid="field-records-set-anchor"
          >
            Tap location…
          </button>
        )}
        {onClose && (
          <button onClick={onClose} className="ml-auto text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="field-records-close">
            <X size={14} />
          </button>
        )}
      </header>

      {/* Capture actions */}
      <div className="px-3 py-2 border-b border-[var(--fop-border)] grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onPickPhoto}
          disabled={!!capturing}
          className="min-h-[56px] px-3 py-2 rounded border border-[var(--fop-primary)]/55 bg-[var(--fop-primary-soft)] text-[var(--fop-text)] inline-flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
          data-testid="field-records-add-photo"
        >
          {capturing === "photo" ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
          Add photo
        </button>
        <input
          ref={fileRef} type="file" accept="image/*" capture="environment"
          onChange={onPhotoChange} className="hidden"
          data-testid="field-records-photo-input"
        />
        <VoiceRecorder
          sheetId={sheetId}
          anchor={anchor}
          onSaved={reload}
          setCapturing={setCapturing}
          capturing={capturing}
        />
      </div>

      {error && (
        <div className="m-3 p-3 rounded border border-[var(--fop-warning)] bg-[var(--fop-warning-soft, rgba(245,158,11,0.08))] text-[12px] text-[var(--fop-text)]" data-testid="field-records-error">
          <AlertOctagon size={12} className="inline mr-1" /> {error}
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading && photos.length === 0 && voices.length === 0 ? (
          <div className="p-4 text-sm text-[var(--fop-text-3)] flex items-center gap-2" data-testid="field-records-loading">
            <Loader2 className="animate-spin" size={14} /> Loading…
          </div>
        ) : (
          <>
            {photos.length === 0 && voices.length === 0 ? (
              <div className="p-6 text-center text-sm text-[var(--fop-text-3)]" data-testid="field-records-empty">
                No field records yet. Tap Add photo or Voice note.
              </div>
            ) : (
              <>
                {photos.length > 0 && (
                  <Section title={`Photos · ${photos.length}`}>
                    {photos.map((p) => (
                      <PhotoRow key={p.id} photo={p} onSend={() => sendPhoto(p.id)} />
                    ))}
                  </Section>
                )}
                {voices.length > 0 && (
                  <Section title={`Voice notes · ${voices.length}`}>
                    {voices.map((v) => (
                      <VoiceRow key={v.id} note={v} onSend={() => sendVoice(v.id)} />
                    ))}
                  </Section>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}


function Section({ title, children }) {
  return (
    <div className="border-b border-[var(--fop-border)] last:border-b-0">
      <div className="px-3 py-1.5 sticky top-0 bg-[var(--fop-surface)] font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}


function PhotoRow({ photo, onSend }) {
  // FP-104 (2026-09-09 audit remediation) -- a plain <img src> can never
  // carry an Authorization header, so this thumbnail was being requested
  // with NO auth against an endpoint that requires it, guaranteeing a
  // 401 (broken image icon) on every field photo. The backend now also
  // accepts a `?token=` query param on this route (mirrors the same
  // pattern already used for sheet rasters/thumbnails); attach it here.
  const _fopToken = (() => {
    try { return localStorage.getItem("fop_bearer") || ""; } catch { return ""; }
  })();
  return (
    <div className="px-3 py-2 border-t border-[var(--fop-border)] first:border-t-0 flex items-start gap-2" data-testid={`field-photo-row-${photo.id}`}>
      <img
        src={`${api.defaults.baseURL}/field-photos/${photo.id}/thumb?token=${encodeURIComponent(_fopToken)}`}
        alt={photo.caption || ""}
        className="w-16 h-16 object-cover rounded border border-[var(--fop-border)]"
      />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] text-[var(--fop-text)] truncate">{photo.caption || "Field photo"}</div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
          {photo.user_name}{photo.gps_lat ? " · GPS" : ""}{photo.nx != null ? ` · ${photo.nx.toFixed(2)},${photo.ny.toFixed(2)}` : ""}
        </div>
        {photo.sent_to_foreman_at ? (
          <div className="font-mono text-[10px] text-[var(--fop-success)] mt-0.5 inline-flex items-center gap-1">
            <CheckCircle2 size={10} /> Sent to foreman
          </div>
        ) : (
          <button onClick={onSend} className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:underline" data-testid={`field-photo-send-${photo.id}`}>
            <Send size={10} /> Send to foreman
          </button>
        )}
      </div>
    </div>
  );
}


function VoiceRow({ note, onSend }) {
  const [audioUrl, setAudioUrl] = useState(null);
  useEffect(() => {
    if (note.byte_size > 0) {
      // Stream from the authenticated endpoint via fetch-blob so the
      // browser respects our Authorization header.
      let revoked = false; let blob_url = null;
      (async () => {
        try {
          const r = await api.get(`/field-voice-notes/${note.id}/audio`, { responseType: "blob" });
          if (revoked) return;
          blob_url = URL.createObjectURL(r.data);
          setAudioUrl(blob_url);
        } catch {}
      })();
      return () => { revoked = true; if (blob_url) URL.revokeObjectURL(blob_url); };
    }
  }, [note.id, note.byte_size]);
  return (
    <div className="px-3 py-2 border-t border-[var(--fop-border)] first:border-t-0" data-testid={`field-voice-row-${note.id}`}>
      <div className="text-[12px] text-[var(--fop-text)]">
        {note.transcript || note.manual_note || "Voice note"}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
        {note.user_name} · {note.transcript_status.replace(/_/g, " ")} · {(note.duration_ms / 1000).toFixed(1)}s
      </div>
      {audioUrl && (
        <audio controls src={audioUrl} className="mt-1 w-full" data-testid={`field-voice-audio-${note.id}`} />
      )}
      {note.sent_to_foreman_at ? (
        <div className="font-mono text-[10px] text-[var(--fop-success)] mt-0.5 inline-flex items-center gap-1">
          <CheckCircle2 size={10} /> Sent to foreman
        </div>
      ) : (
        <button onClick={onSend} className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:underline" data-testid={`field-voice-send-${note.id}`}>
          <Send size={10} /> Send to foreman
        </button>
      )}
    </div>
  );
}


function VoiceRecorder({ sheetId, anchor, onSaved, setCapturing, capturing }) {
  const [state, setState] = useState("idle"); // idle | recording | saving
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState("");
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const speechRef = useRef(null);
  const startedAt = useRef(0);
  const tickRef = useRef(null);

  const start = async () => {
    if (state !== "idle") return;
    setTranscript("");
    setElapsed(0);
    setCapturing("voice");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.start();
      // Web Speech transcript (best effort, not all browsers).
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SR) {
        const sr = new SR();
        sr.interimResults = true;
        sr.continuous = true;
        sr.onresult = (ev) => {
          let txt = "";
          for (let i = ev.resultIndex; i < ev.results.length; i++) {
            txt += ev.results[i][0].transcript;
          }
          setTranscript(txt.trim());
        };
        try { sr.start(); speechRef.current = sr; } catch {}
      }
      startedAt.current = performance.now();
      tickRef.current = setInterval(() => setElapsed(Math.round(performance.now() - startedAt.current)), 200);
      setState("recording");
    } catch (e) {
      setCapturing(null);
      confirm.raw?.error?.("MICROPHONE ACCESS BLOCKED — ENABLE MICROPHONE OR TYPE NOTE");
    }
  };

  const stop = async () => {
    if (state !== "recording") return;
    setState("saving");
    if (tickRef.current) clearInterval(tickRef.current);
    const rec = recRef.current;
    try { speechRef.current?.stop(); } catch {}
    try { speechRef.current = null; } catch {}
    const duration = Math.round(performance.now() - startedAt.current);
    const blob = await new Promise((resolve) => {
      rec.addEventListener("stop", () => {
        const b = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        rec.stream.getTracks().forEach((t) => t.stop());
        resolve(b);
      }, { once: true });
      rec.stop();
    });
    try {
      const form = new FormData();
      form.append("file", new File([blob], "voice.webm", { type: blob.type || "audio/webm" }));
      if (anchor) { form.append("nx", String(anchor.nx)); form.append("ny", String(anchor.ny)); }
      form.append("duration_ms", String(duration));
      if (transcript) {
        form.append("transcript", transcript);
        form.append("transcript_status", "ready");
      } else {
        form.append("transcript_status", "audio_saved");
      }
      await api.post(`/sheets/${sheetId}/field-voice-notes`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      confirm.raw?.success?.(transcript ? "Voice note saved with transcript" : "Voice note saved");
      setTranscript("");
      onSaved?.();
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "VOICE NOTE UPLOAD FAILED — RETRY");
    } finally {
      setState("idle");
      setCapturing(null);
    }
  };

  return (
    <button
      type="button"
      onClick={state === "recording" ? stop : start}
      disabled={capturing && capturing !== "voice"}
      className={`min-h-[56px] px-3 py-2 rounded border inline-flex items-center gap-2 text-sm font-semibold disabled:opacity-50 ${
        state === "recording"
          ? "border-[var(--fop-danger,#dc2626)]/55 bg-[var(--fop-danger-soft, rgba(220,38,38,0.08))]"
          : "border-[var(--fop-primary)]/55 bg-[var(--fop-primary-soft)]"
      } text-[var(--fop-text)]`}
      data-testid="field-records-voice-toggle"
      data-state={state}
    >
      {state === "recording"
        ? <Square size={16} className="text-[var(--fop-danger,#dc2626)]" />
        : state === "saving"
          ? <Loader2 size={16} className="animate-spin" />
          : <Mic size={16} />}
      <span className="flex-1 text-left">
        {state === "recording"
          ? `Stop · ${(elapsed/1000).toFixed(1)}s`
          : state === "saving"
            ? "Saving…"
            : "Voice note"}
      </span>
    </button>
  );
}
